import { useMemo } from 'react'
import { notifications } from '@mantine/notifications'
import { useQueryClient } from '@tanstack/react-query'

import { INVALIDATE_KEYS } from '@/constants/invalidateGoalQueryKeys.constants'

import {
  useGoalsServiceGoalsControllerClaimAmount,
  useGoalsServiceGoalsControllerGet,
  useNotificationsServiceNotificationControllerGetAllKey,
  useNotificationsServiceNotificationControllerMarkAsRead,
} from '@/__generated__/queries'

export const useRejectNotification = (id: number, showNotification = true) => {
  const queryClient = useQueryClient()

  const rejectMutation =
    useNotificationsServiceNotificationControllerMarkAsRead({
      onSuccess: () => {
        if (showNotification) {
          notifications.show({
            color: 'green',
            message: 'Reminder has been successfully rejected',
            title: 'Reminder rejected',
          })
        }

        queryClient.invalidateQueries({
          queryKey: [useNotificationsServiceNotificationControllerGetAllKey],
        })
      },
    })

  return {
    rejectNotification: () => rejectMutation.mutate({ notificationId: id }),
  }
}

export const useAcceptNotification = (id: number, goalId: number) => {
  const queryClient = useQueryClient()

  const { rejectNotification } = useRejectNotification(id, false)

  const { data } = useGoalsServiceGoalsControllerGet({
    goalId,
  })

  const goalMonthlyAmount = useMemo<number>(
    () => data?.monthlyReminderAmount ?? 0,
    [data],
  )

  const acceptMutation = useGoalsServiceGoalsControllerClaimAmount({
    onError: () => {
      notifications.show({
        color: 'red',
        message: 'Failed to claim amount',
        title: 'Error',
      })
    },
    onSuccess: () => {
      notifications.show({
        color: 'green',
        message: 'Amount has been successfully claimed',
        title: 'Amount claimed',
      })

      INVALIDATE_KEYS.forEach(key => {
        queryClient.invalidateQueries({
          queryKey: [key],
        })
      })

      rejectNotification()
    },
  })

  return {
    acceptNotification: () => {
      acceptMutation.mutate({
        goalId,
        requestBody: { amount: goalMonthlyAmount },
      })
    },
  }
}
